'use client';

import React from 'react';
import { v4 as uuidV4 } from 'uuid';
import throttle from 'lodash/throttle';
import { usePathname, useSearchParams } from 'next/navigation';
import GlobalContext from '@/context/global-context';
import { TRACK_NAME } from '@/constants/site-track';
import { LOGIN_ROUTER_PATH, PRODUCTION_HOST } from '@/constants/router-path';
import {
  _BI_COOKIE_NAME,
  CID_COOKIE_NAME,
  SID_COOKIE_NAME,
  SESSION_START_STORAGE_NAME,
  REFERRER_STORAGE_NAME,
  BEFORE_LOGIN_REFERRER_STORAGE_NAME,
} from '@/constants/site-track';
import {
  getCookie,
  getSessionStorage,
  setCookie,
  setSessionStorage,
} from '@/utils/storage';
import { sendEvent, useIntersectionObserver } from '@/utils/site-track';
import { isTimeStamp } from '@/utils/common-utils';

const SiteTrack = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams()?.toString() || '';

  const [trackObserverElements, setTrackObserverElements] = React.useState<
    Element[]
  >([]);
  const [historyUrl, setHistoryUrl] = React.useState<string[]>([]);

  const hasMoved = React.useRef(false);
  const hasFirstVisited = React.useRef(false);

  const trackMove = React.useCallback(
    throttle(() => {
      if (!hasMoved.current) {
        setCookie(_BI_COOKIE_NAME, Date.now().toString());
        hasMoved.current = true;
        sendEvent({ name: TRACK_NAME.move });
      }
    }, 300),
    [],
  );

  useIntersectionObserver(trackObserverElements);

  React.useEffect(() => {
    const referrer = document.referrer;
    const isLoginUrl = referrer.includes(window.origin + LOGIN_ROUTER_PATH);
    if (referrer) {
      if (!isLoginUrl) {
        GlobalContext.setContext(REFERRER_STORAGE_NAME, referrer);
        setHistoryUrl([referrer]);
      } else {
        const beforeLoginUrl = sessionStorage.getItem(
          BEFORE_LOGIN_REFERRER_STORAGE_NAME,
        );
        if (beforeLoginUrl) {
          GlobalContext.setContext(REFERRER_STORAGE_NAME, beforeLoginUrl);
          setHistoryUrl([beforeLoginUrl]);
        }
        sessionStorage.removeItem(BEFORE_LOGIN_REFERRER_STORAGE_NAME);
      }
    }
    const cid = getCookie(CID_COOKIE_NAME);
    if (!hasFirstVisited.current && !cid) {
      hasFirstVisited.current = true;
      const cid = uuidV4().replaceAll('-', '');
      const sid = uuidV4().replaceAll('-', '');
      sessionStorage.setItem(CID_COOKIE_NAME, cid);
      sessionStorage.setItem(SID_COOKIE_NAME, sid);
      sendEvent({ name: TRACK_NAME.first_visit });
    }
    const session_start = getSessionStorage(SESSION_START_STORAGE_NAME);
    if (!session_start) {
      setSessionStorage(SESSION_START_STORAGE_NAME, Date.now().toString());
      sendEvent({ name: TRACK_NAME.session_start });
    }
    const _bi = getCookie(_BI_COOKIE_NAME);
    const isValidDate = _bi && isTimeStamp(_bi);
    if (!hasMoved.current && !isValidDate) {
      document.addEventListener('mousemove', trackMove);
      document.addEventListener('touchmove', trackMove);
    } else {
      hasMoved.current = true;
      document.removeEventListener('mousemove', trackMove);
      document.removeEventListener('touchmove', trackMove);
    }
    return () => {
      hasMoved.current = false;
      document.removeEventListener('mousemove', trackMove);
      document.removeEventListener('touchmove', trackMove);
    };
  }, []);

  React.useEffect(() => {
    if (pathname) {
      const renderEle = document.querySelector('body div');
      if (!renderEle) {
        return;
      }
      historyUrl.length > 0 &&
        GlobalContext.setContext(
          REFERRER_STORAGE_NAME,
          historyUrl[historyUrl.length - 1],
        );
      const currentUrl = document.URL;
      const isLoginUrl = currentUrl.includes(window.origin + LOGIN_ROUTER_PATH);
      !isLoginUrl && setHistoryUrl((prev) => [...prev, currentUrl]);
      if (window.location.host === PRODUCTION_HOST) {
        const url = searchParams ? `${pathname}?${searchParams}` : pathname;
        try {
          (window as any)._hmt.push([TRACK_NAME.baidu_track, url]);
        } catch (e) {}
      }
      sendEvent({ name: TRACK_NAME.page_view });
      const obsElements = document.querySelectorAll('[data-observer-id]');
      setTrackObserverElements(obsElements as any);
    }
  }, [pathname, searchParams]);

  return null;
};

export default SiteTrack;
